import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { UseMutationResult } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  isQueueRetired,
  isTaskConflict,
} from "../features/queue/invalidateTaskViews";
import {
  requestErrorText,
  type TFunction,
} from "../features/queue/requestErrorText";

// Набор i18n-ключей одной кнопки действия (все под `task.*`).
export interface TaskActionKeys {
  readonly button: string;
  readonly inFlight: string;
  readonly confirmTitle: string;
  readonly confirmBody: string;
  readonly confirmAccept: string;
  readonly confirmReject: string;
  readonly errorTitle: string;
}

interface TaskActionButtonProps {
  readonly mutation: UseMutationResult<void, Error, void>;
  readonly keys: TaskActionKeys;
  readonly color?: string | undefined;
}

// Текст Alert'а в окне по причине отказа.
function actionErrorText(error: unknown, t: TFunction): string {
  if (isQueueRetired(error)) {
    return t("task.rerunQueueRetired");
  }
  return requestErrorText(error, t);
}

// Кнопка действия над задачей + confirm-Modal. Общая для отмены,
// перезапуска и снятия паузы:
// - 409 = «состояние задачи уже изменилось»: окно закрывается,
//   уведомление показывает и задачу перечитывает хук мутации;
// - 409 `queue_retired` (очередь снята, только у перезапуска): Alert с
//   причиной в окне, окно не закрывается, перечитывания нет, подтверждение
//   выключено — повтор не поможет;
// - прочие отказы — Alert в окне (HTTP-код или «нет связи»);
// - пока запрос идёт, окно закрыть нельзя (иначе результат потеряется
//   из виду): Esc, клик мимо и крестик выключены, «Нет» — disabled;
// - ошибка прошлой попытки сбрасывается при открытии окна.
export function TaskActionButton({
  mutation,
  keys,
  color,
}: TaskActionButtonProps): ReactElement {
  const { t } = useTranslation();
  const [confirmOpened, confirmHandlers] = useDisclosure(false);
  const isPending = mutation.isPending;

  const handleOpen = (): void => {
    mutation.reset();
    confirmHandlers.open();
  };

  const handleConfirm = (): void => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        confirmHandlers.close();
      },
      onError: (error) => {
        // Перечитывание и уведомление на 409 делает хук мутации; окно — дело
        // кнопки (размонтирована — окна уже нет).
        if (isTaskConflict(error)) {
          confirmHandlers.close();
        }
      },
    });
  };

  const error = mutation.error;
  const showError = mutation.isError && !isTaskConflict(error);
  const retryUseless = mutation.isError && isQueueRetired(error);

  return (
    <>
      <Button
        {...(color === undefined ? {} : { color })}
        variant="light"
        onClick={handleOpen}
        loading={isPending}
      >
        {isPending ? t(keys.inFlight) : t(keys.button)}
      </Button>
      <Modal
        opened={confirmOpened}
        onClose={confirmHandlers.close}
        closeOnEscape={!isPending}
        closeOnClickOutside={!isPending}
        withCloseButton={!isPending}
        title={t(keys.confirmTitle)}
        centered
      >
        <Stack gap="md">
          <Text>{t(keys.confirmBody)}</Text>
          {showError ? (
            <Alert color="red" title={t(keys.errorTitle)}>
              {actionErrorText(error, t)}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={confirmHandlers.close}
              disabled={isPending}
            >
              {t(keys.confirmReject)}
            </Button>
            <Button
              {...(color === undefined ? {} : { color })}
              onClick={handleConfirm}
              loading={isPending}
              disabled={retryUseless}
            >
              {t(keys.confirmAccept)}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
